import React, { useEffect, useMemo, useRef, useState } from "react";
import LoadingV2 from "../../components/LoadingV2";
import EmptyTableIconV2 from "../../images/EmptyTableV2";
import TagStatus from "../../components/TagStatus";
import {
  BORDER_STRONG,
  BottomSheet,
  CONTROL,
  ConfirmSheet,
  Icon,
  MobileCard,
  RADIUS_INPUT,
  SheetField,
  StickyFooter,
  STICKY_FOOTER_HEIGHT,
  TAP,
  TEAL,
  TEAL_TINT,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  inputStyle,
  t,
} from "../../components/mobile";
import {
  RESERVATION_SOURCE_LABELS,
  RESERVATION_STATUS_LABELS,
  ReservationSource,
  ReservationStatus,
  TableReservation,
} from "../../services/tableReservations.service";
import {
  SheetRow,
  StatusChoice,
  formatDateTime,
  formatDayMonth,
  formatTime,
  statusSegments,
  useLocationTables,
} from "./reservationShared";

/**
 * Table Reservations on touch viewports (< 1024px): one vertical scroll
 * surface, a sticky search + Filters row over a sticky status segmented row,
 * bookings as cards, and "Create reservation" pinned in a sticky footer.
 *
 * Phone (< 700px) stacks the cards in one column. Tablet (700–1023px, the
 * front-of-house iPad) lays the same cards out two-up: a booking card is a
 * self-contained record, so a second column doubles the bookings on screen
 * instead of leaving half the iPad empty. Both widths share every control —
 * only the card grid and the sheet row width differ.
 *
 * Tapping a card opens the record sheet: the full booking plus a one-tap
 * status change (the front-of-house action), with "Open booking" for the
 * detail screen. Cancelling or marking a no-show confirms in a sheet that
 * names the guest.
 */

interface Props {
  language: any;
  rows: TableReservation[];
  count: number;
  page: number;
  limit: number;
  loading: boolean;
  locations: { id: string; name: string }[];
  filter: {
    search: string;
    locationId: string;
    status: string;
    source: string;
    dateFrom: string;
  };
  isTablet: boolean;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onLocationChange: (id: string) => void;
  onSourceChange: (value: string) => void;
  onDateChange: (date: Date | null) => void;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
  onCreate: () => void;
  onOpen: (id: string) => void;
  /** Quick status change from the record sheet; resolves once the PATCH lands. */
  onQuickStatus: (row: TableReservation, status: ReservationStatus) => Promise<void>;
  emptyState: { title?: string; desc?: string };
}

/** Statuses that end a booking and therefore confirm before they are applied. */
const CONFIRMING_STATUSES: ReservationStatus[] = [
  ReservationStatus.Cancelled,
  ReservationStatus.NoShow,
];

const TableReservationsMobile: React.FC<Props> = ({
  language,
  rows,
  count,
  page,
  limit,
  loading,
  locations,
  filter,
  isTablet,
  onSearchChange,
  onStatusChange,
  onLocationChange,
  onSourceChange,
  onDateChange,
  onClearFilters,
  onPageChange,
  onCreate,
  onOpen,
  onQuickStatus,
  emptyState,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [recordId, setRecordId] = useState<string>("");
  const [locationQuery, setLocationQuery] = useState<string>("");
  const [pendingStatus, setPendingStatus] = useState<ReservationStatus | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);

  const record = useMemo(() => rows.find((r) => r.id === recordId), [rows, recordId]);

  /**
   * The assigned table, resolved only while a record with one is open — the
   * table is the first thing a host checks before seating, and the list
   * payload carries the id alone. No request fires for a booking with no
   * table, or while the sheet is closed.
   */
  const { tables: recordTables, loading: recordTablesLoading } = useLocationTables(
    record?.floorPlanDetailId ? record.locationId : "",
    language,
  );
  const recordTableLabel = record?.floorPlanDetailId
    ? recordTables.find((table) => table.id === record.floorPlanDetailId)?.label ??
      (recordTablesLoading
        ? t(language, "Loading…")
        : t(language, "Table no longer available"))
    : t(language, "No table assigned");
  const locationsById = useMemo(() => {
    const map: Record<string, string> = {};
    locations.forEach((l) => (map[l.id] = l.name));
    return map;
  }, [locations]);

  const visibleLocations = locationQuery.trim()
    ? locations.filter((l) =>
        String(l.name ?? "").toLowerCase().includes(locationQuery.trim().toLowerCase()),
      )
    : locations;

  const activeFilterCount =
    (filter.locationId ? 1 : 0) + (filter.source ? 1 : 0) + (filter.dateFrom ? 1 : 0);
  const hasQuery = !!filter.search.trim() || activeFilterCount > 0 || !!filter.status;
  const totalPages = Math.max(1, Math.ceil((count || 0) / (limit || 50)));

  const statusOptions = [
    { value: "", label: t(language, "All") },
    ...statusSegments(language),
  ];
  const sourceEntries = Object.entries(RESERVATION_SOURCE_LABELS);

  const pad = (n: number) => String(n).padStart(2, "0");
  const dateFilterValue = filter.dateFrom
    ? (() => {
        const d = new Date(filter.dateFrom);
        return Number.isNaN(d.getTime())
          ? ""
          : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      })()
    : "";

  // A new page starts at its first booking: paging from the bottom of one
  // page used to leave the next one scrolled to its end.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [page]);

  const pageButton = (label: string, disabled: boolean, onClick: () => void, flip?: boolean) => (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex items-center justify-center"
      style={{
        width: `${TAP}px`,
        height: `${TAP}px`,
        borderRadius: RADIUS_INPUT,
        border: `1px solid ${BORDER_STRONG}`,
        backgroundColor: "#FFFFFF",
        opacity: disabled ? 0.4 : 1,
        transform: flip ? "rotate(180deg)" : undefined,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <Icon name="chev" size={16} color={TEXT} />
    </button>
  );

  const applyStatus = async (row: TableReservation, next: ReservationStatus) => {
    setSavingStatus(true);
    try {
      await onQuickStatus(row, next);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleRecordStatus = (value: string) => {
    if (!record) return;
    const next = Number(value) as ReservationStatus;
    if (next === record.status) return;
    if (CONFIRMING_STATUSES.includes(next)) {
      setPendingStatus(next);
      return;
    }
    void applyStatus(record, next);
  };

  return (
    <div className="flex flex-col h-full min-h-0" style={{ position: "relative" }}>
      <div
        ref={scrollRef}
        className="scroll-container"
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          paddingBottom: `${STICKY_FOOTER_HEIGHT}px`,
        }}
      >
        <p
          className="font-semibold font-inter"
          style={{ color: TEXT, fontSize: "22px", lineHeight: "28px" }}
        >
          {t(language, "Table Reservations")}
        </p>

        {/* One sticky block: search + Filters, then the status pills. */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            backgroundColor: "#FFFFFF",
            paddingTop: "12px",
          }}
        >
          <div className="flex items-center" style={{ gap: "8px" }}>
            <div
              className="flex items-center"
              style={{
                flex: 1,
                minWidth: 0,
                height: `${CONTROL}px`,
                border: `1px solid ${BORDER_STRONG}`,
                borderRadius: RADIUS_INPUT,
                gap: "8px",
                padding: "0 12px",
              }}
            >
              <Icon name="search" size={18} />
              <input
                type="search"
                inputMode="search"
                enterKeyHint="search"
                aria-label={t(language, "Name, email or phone")}
                value={filter.search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t(language, "Name, email or phone")}
                className="font-inter placeholder:text-placeholder bg-transparent outline-none"
                style={{
                  fontSize: "16px",
                  flex: 1,
                  minWidth: 0,
                  color: TEXT,
                  height: "100%",
                  border: "none",
                  WebkitAppearance: "none",
                }}
              />
              {filter.search && (
                <button
                  type="button"
                  aria-label={t(language, "Clear search")}
                  onClick={() => onSearchChange("")}
                  className="flex items-center justify-center"
                  style={{
                    // 44px wide, not 32: the whole affordance is a tap target.
                    // The negative margin keeps the glyph optically where the
                    // 12px field padding puts it.
                    width: `${TAP}px`,
                    height: `${CONTROL}px`,
                    border: "none",
                    background: "transparent",
                    marginRight: "-12px",
                    cursor: "pointer",
                  }}
                >
                  <Icon name="close" size={16} />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="flex items-center font-inter"
              style={{
                flex: "none",
                height: `${TAP}px`,
                padding: "0 14px",
                gap: "6px",
                borderRadius: "30px",
                border: `1px solid ${activeFilterCount ? TEAL : BORDER_STRONG}`,
                backgroundColor: activeFilterCount ? TEAL_TINT : "#FFFFFF",
                color: TEAL,
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <Icon name="sliders" color={TEAL} size={18} />
              <span>{t(language, "Filters")}</span>
              {activeFilterCount > 0 && (
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    minWidth: "20px",
                    height: "20px",
                    padding: "0 6px",
                    borderRadius: "999px",
                    backgroundColor: TEAL,
                    color: "#FFFFFF",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          <div style={{ padding: "8px 0" }}>
            <StatusChoice
              label={t(language, "Status")}
              sheetTitle={t(language, "Filter by status")}
              options={statusOptions}
              value={filter.status}
              onChange={onStatusChange}
              pillsFit={isTablet}
            />
          </div>
        </div>

        <div
          style={{
            position: "relative",
            minHeight: loading ? "240px" : undefined,
            overflow: loading ? "hidden" : undefined,
          }}
        >
          {loading ? (
            <LoadingV2 />
          ) : rows.length === 0 ? (
            <div
              className="flex justify-center items-center"
              style={{
                flexDirection: "column",
                backgroundColor: "#F8F8F8",
                borderRadius: "16px",
                padding: "24px",
                marginTop: "8px",
              }}
            >
              <div className="flex items-center justify-center" style={{ width: "48px", height: "48px" }}>
                <EmptyTableIconV2 />
              </div>
              <div className="space-y-1" style={{ marginTop: "12px", textAlign: "center" }}>
                <p className="font-inter" style={{ fontSize: "14px", fontWeight: 600, lineHeight: "20px", color: TEXT }}>
                  {hasQuery ? t(language, "No Results Found") : emptyState.title}
                </p>
                <p className="font-inter" style={{ fontSize: "14px", lineHeight: "20px", color: TEXT_DIM }}>
                  {hasQuery ? t(language, "Try another search or clear the filters") : emptyState.desc}
                </p>
              </div>
            </div>
          ) : (
            <div
              style={
                isTablet
                  ? {
                      display: "grid",
                      // minmax(0,…): a plain `1fr` track is floored at the
                      // card's min-content width, so one long guest or
                      // location name widened a column past half the row and
                      // pushed the right-hand cards' time, date and chevron
                      // off a 768 iPad. The cards ellipsise instead.
                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                      columnGap: "24px",
                    }
                  : { display: "flex", flexDirection: "column" }
              }
            >
              {rows.map((row, index) => {
                const statusLabel =
                  RESERVATION_STATUS_LABELS[row.status as ReservationStatus] ?? "-";
                const locationName = locationsById[row.locationId] ?? "-";
                const guests = `${row.partySize} ${
                  row.partySize === 1 ? t(language, "guest") : t(language, "guests")
                }`;
                return (
                  <MobileCard
                    key={row.id}
                    title={
                      <span className="flex items-center" style={{ gap: "8px", minWidth: 0 }}>
                        <span
                          style={{
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.customerName || "-"}
                        </span>
                        <span style={{ flex: "none" }}>
                          <TagStatus value={statusLabel} label={t(language, statusLabel)} />
                        </span>
                      </span>
                    }
                    subtitle={`${row.reservationNumber} · ${guests}`}
                    subtitle2={locationName}
                    trailing={formatTime(row.reservedFrom)}
                    trailingSub={formatDayMonth(row.reservedFrom)}
                    onClick={() => setRecordId(row.id)}
                    last={isTablet ? index >= rows.length - 2 : index === rows.length - 1}
                  />
                );
              })}
            </div>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between" style={{ padding: "12px 0 16px" }}>
            {pageButton(t(language, "Previous"), page <= 1, () => onPageChange(page - 1), true)}
            <span className="font-inter" style={{ fontSize: "13px", color: TEXT_DIM, textAlign: "center" }}>
              {t(language, "Showing")} {(page - 1) * limit + 1}–{Math.min(page * limit, count)}{" "}
              {t(language, "of")} {count}
            </span>
            {pageButton(t(language, "Next"), page >= totalPages, () => onPageChange(page + 1))}
          </div>
        )}
      </div>

      <StickyFooter primaryLabel={`+ ${t(language, "Create reservation")}`} onPrimary={onCreate} />

      {/* Filters. */}
      <BottomSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t(language, "Filters")}
        footer={
          <StickyFooter
            position="static"
            cancelLabel={t(language, "Clear all")}
            onCancel={onClearFilters}
            primaryLabel={`${t(language, "Show")} ${count} ${t(language, "bookings")}`}
            onPrimary={() => setFiltersOpen(false)}
          />
        }
      >
        <SheetSection label={t(language, "Reserved on")}>
          <input
            type="date"
            value={dateFilterValue}
            onChange={(e) => {
              if (!e.target.value) {
                onDateChange(null);
                return;
              }
              const [y, m, d] = e.target.value.split("-").map(Number);
              onDateChange(new Date(y, m - 1, d));
            }}
            aria-label={t(language, "Reserved on")}
            className="font-inter"
            style={inputStyle}
          />
        </SheetSection>

        <SheetSection label={t(language, "Source")}>
          <div className="flex items-center" style={{ gap: "8px", flexWrap: "wrap" }}>
            {[["", t(language, "All")] as [string, string]]
              .concat(sourceEntries.map(([v, l]) => [v, t(language, l)] as [string, string]))
              .map(([value, label]) => {
                const active = filter.source === value;
                return (
                  <button
                    key={value || "all"}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => onSourceChange(value)}
                    className="font-inter"
                    style={{
                      height: `${TAP}px`,
                      padding: "0 16px",
                      borderRadius: "30px",
                      fontSize: "14px",
                      fontWeight: 500,
                      border: `1px solid ${active ? TEAL : BORDER_STRONG}`,
                      backgroundColor: active ? TEAL : "#FFFFFF",
                      color: active ? "#FFFFFF" : TEXT,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
          </div>
        </SheetSection>

        <SheetSection label={t(language, "Location")}>
          {locations.length > 6 ? (
            <div
              className="flex items-center"
              style={{
                height: `${CONTROL}px`,
                border: `1px solid ${BORDER_STRONG}`,
                borderRadius: RADIUS_INPUT,
                gap: "8px",
                padding: "0 12px",
                marginBottom: "8px",
              }}
            >
              <Icon name="search" size={18} />
              <input
                type="search"
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                placeholder={t(language, "Search location")}
                aria-label={t(language, "Search location")}
                className="font-inter placeholder:text-placeholder bg-transparent outline-none"
                style={{
                  fontSize: "16px",
                  flex: 1,
                  minWidth: 0,
                  color: TEXT,
                  height: "100%",
                  border: "none",
                  WebkitAppearance: "none",
                }}
              />
            </div>
          ) : undefined}
        </SheetSection>
        <SheetRow
          label={t(language, "All locations")}
          selected={!filter.locationId}
          onClick={() => onLocationChange("")}
        />
        {visibleLocations.map((loc, index) => (
          <SheetRow
            key={loc.id}
            label={loc.name}
            selected={filter.locationId === loc.id}
            last={index === visibleLocations.length - 1}
            onClick={() => onLocationChange(loc.id)}
          />
        ))}
      </BottomSheet>

      {/* Record sheet. */}
      <BottomSheet
        open={!!record && pendingStatus === null}
        onClose={() => setRecordId("")}
        title={record?.customerName || t(language, "Booking")}
        footer={
          record ? (
            <StickyFooter
              position="static"
              cancelLabel={t(language, "Close")}
              onCancel={() => setRecordId("")}
              primaryLabel={t(language, "Open booking")}
              onPrimary={() => onOpen(record.id)}
            />
          ) : undefined
        }
      >
        {record && (
          <div style={{ padding: "0 20px 8px" }}>
            <div className="flex flex-col" style={{ gap: "4px", marginBottom: "16px" }}>
              <SheetField label={t(language, "Reservation #")} value={record.reservationNumber} />
              <SheetField
                label={t(language, "Reserved from")}
                value={formatDateTime(record.reservedFrom)}
              />
              <SheetField
                label={t(language, "Reserved to")}
                value={formatDateTime(record.reservedTo)}
              />
              <SheetField label={t(language, "Party size")} value={String(record.partySize)} />
              <SheetField
                label={t(language, "Location")}
                value={locationsById[record.locationId] ?? "-"}
              />
              <SheetField label={t(language, "Table")} value={recordTableLabel} />
              <SheetField
                label={t(language, "Source")}
                value={t(
                  language,
                  RESERVATION_SOURCE_LABELS[record.source as ReservationSource] ?? "-",
                )}
              />
              {record.customerPhone && (
                <SheetField
                  label={t(language, "Customer phone")}
                  value={
                    <a
                      href={`tel:${record.customerPhone}`}
                      className="inline-flex items-center"
                      // The one "call the guest" action on the sheet: a full
                      // 44px tall target, not a 16px line of text.
                      style={{ color: TEAL, minHeight: `${TAP}px`, fontWeight: 500 }}
                    >
                      {record.customerPhone}
                    </a>
                  }
                />
              )}
              {record.customerEmail && (
                <SheetField label={t(language, "Customer email")} value={record.customerEmail} />
              )}
              {record.notes && <SheetField label={t(language, "Notes")} value={record.notes} />}
            </div>
            {/* The pill row has no label of its own; the sheet variant of
                StatusChoice prints "Status" inside the field itself. */}
            {isTablet && (
              <p
                className="font-inter"
                style={{ fontSize: "13px", fontWeight: 500, color: TEXT_MUTED, marginBottom: "6px" }}
              >
                {t(language, "Status")}
              </p>
            )}
            <StatusChoice
              label={t(language, "Status")}
              sheetTitle={t(language, "Change status")}
              options={statusSegments(language)}
              value={String(record.status)}
              onChange={handleRecordStatus}
              pillsFit={isTablet}
            />
            {savingStatus && (
              <p className="font-inter" style={{ marginTop: "8px", fontSize: "13px", color: TEXT_DIM }}>
                {t(language, "Saving…")}
              </p>
            )}
          </div>
        )}
      </BottomSheet>

      <ConfirmSheet
        open={pendingStatus !== null && !!record}
        title={
          pendingStatus === ReservationStatus.Cancelled
            ? t(language, "Cancel booking")
            : t(language, "Mark as no show")
        }
        message={
          record
            ? `${
                pendingStatus === ReservationStatus.Cancelled
                  ? t(language, "Cancel the booking for")
                  : t(language, "Mark a no show for")
              } ${record.customerName} · ${record.reservationNumber} (${formatDateTime(
                record.reservedFrom,
              )})?`
            : ""
        }
        confirmLabel={
          pendingStatus === ReservationStatus.Cancelled
            ? t(language, "Cancel booking")
            : t(language, "Mark as no show")
        }
        cancelLabel={t(language, "Keep booking")}
        onCancel={() => setPendingStatus(null)}
        onConfirm={() => {
          const next = pendingStatus;
          const row = record;
          setPendingStatus(null);
          if (row && next !== null) void applyStatus(row, next);
        }}
      />
    </div>
  );
};

/**
 * A labelled block inside the filters sheet. The Location heading has no body
 * of its own below six locations (its search box only appears past that) and
 * is followed by full-bleed `SheetRow`s, so an empty section keeps the same
 * 8px gap to what follows instead of adding the 16px a filled one ends with.
 */
const SheetSection: React.FC<{ label: string; children?: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div style={{ padding: children ? "0 20px 16px" : "0 20px 8px" }}>
    <p
      className="font-inter"
      style={{
        fontSize: "13px",
        fontWeight: 500,
        color: TEXT_MUTED,
        marginBottom: children ? "8px" : 0,
      }}
    >
      {label}
    </p>
    {children}
  </div>
);

export default TableReservationsMobile;
